/**
 * The domain callout — the loudest thing on /welcome until the site is on a
 * name the client owns.
 *
 * This is the reason the screen exists. Everything else on the page is status;
 * this is the one place the client either points a domain they already have or
 * buys one, and it is the step that ends the setup flow. It sits above the
 * "happening now" list on purpose.
 *
 * It never invents a flow: the button opens the real `?task=domain` panel,
 * which checks availability against `/api/domain/check-availability` and
 * records the client's choice through `/api/setup/tasks/domain`.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Eyebrow, Mono, Panel, PanelBody, Tag } from '../ui/kit';
import type { SetupStatus } from '../../lib/setupFlow';

function GlobeMark() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.5}>
      <circle cx="12" cy="12" r="9" strokeLinecap="round" strokeLinejoin="round" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.5 12h17M12 3.5c2 2.5 3.5 5.2 3.5 8.5s-1.5 6-3.5 8.5c-2-2.5-3.5-5.2-3.5-8.5s1.5-6 3.5-8.5z"
      />
    </svg>
  );
}

const COPY: Record<
  Exclude<SetupStatus['domainStatus'], 'connected'>,
  { eyebrow: string; headline: string; body: string; cta: string }
> = {
  none: {
    eyebrow: 'Your own domain',
    headline: 'Put your site on your own name.',
    body: 'Right now customers would have to type a Foundlio address. Point a domain you already own at your site, or tell us the name you want and we go and get it.',
    cta: 'Connect or buy a domain',
  },
  pending: {
    eyebrow: 'In progress',
    headline: "We've got the name. Nothing on you.",
    body: 'DNS and the certificate can take anywhere from a few minutes to a few hours. We watch it and switch your site over the moment it lands.',
    cta: 'Check the details',
  },
  deferred: {
    eyebrow: 'When you are ready',
    headline: 'You can move to your own domain any time.',
    body: 'Your site works exactly as it does today on your Foundlio address. Swapping it for a name of your own takes one step and no downtime.',
    cta: 'Get a domain',
  },
};

export default function DomainCallout({
  status,
  isLastStep = false,
  onOpen,
}: {
  status: SetupStatus;
  /** True when nothing else is still on the client — changes the eyebrow only. */
  isLastStep?: boolean;
  /** Opens the real `?task=domain` panel. */
  onOpen: () => void;
}) {
  if (status.domainStatus === 'connected') return null;

  const base = COPY[status.domainStatus];
  const copy =
    isLastStep && status.domainStatus === 'none' ? { ...base, eyebrow: 'Last step' } : base;
  const previewHost = status.previewUrl?.replace(/^https?:\/\//, '').replace(/\/$/, '') ?? status.domain;
  const urgent = status.domainStatus === 'none';

  return (
    <Panel className={urgent ? 'border-primary/40' : undefined}>
      <PanelBody className="space-y-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 text-primary" aria-hidden>
            <GlobeMark />
          </span>
          <div className="min-w-0 flex-1">
            <Eyebrow className="mb-1.5">{copy.eyebrow}</Eyebrow>
            <h3 className="font-display text-xl font-bold tracking-[-0.03em] text-primary sm:text-[1.4rem]">
              {copy.headline}
            </h3>
          </div>
        </div>

        <p className="text-sm leading-relaxed text-muted">{copy.body}</p>

        {previewHost && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-zinc-200 bg-paper-tint px-3.5 py-2.5">
            <Tag tone="neutral">Today</Tag>
            <Mono className="min-w-0 break-all text-[12px] text-zinc-600">{previewHost}</Mono>
          </div>
        )}

        <motion.button
          type="button"
          onClick={onOpen}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          className={
            urgent
              ? 'block w-full rounded-lg bg-primary px-4 py-3 text-center text-sm font-semibold text-white transition-opacity hover:opacity-90'
              : 'block w-full rounded-lg border border-zinc-300 px-4 py-3 text-center text-sm font-semibold text-primary transition-colors hover:border-primary'
          }
        >
          {copy.cta}
        </motion.button>

        {urgent && (
          <p className="text-center font-mono text-[11px] uppercase tracking-eyebrow text-zinc-400">
            Already own one? It takes two DNS records
          </p>
        )}
      </PanelBody>
    </Panel>
  );
}
